export class Domains {
  /**
   *
   */
  private readonly domains: string[];
  /**
   * list of staging subdomain
   */
  private readonly devSubDomains: string[];
  /**
   * list of staging subdomain
   */
  private readonly stagingSubDomains: string[];
  /**
   * list of prod subdomain
   */
  private readonly prodSubDomains: string[];
  /**
   * special urls for api calls
   *
   * @example {softswiss: 'www', geoip: 'geoip'}
   */
  private readonly specialSubDomains: {
    softswiss: string;
    geoip: string;
    blockchain: string;
    promo: string[];
  };

  /**
   * special urls for api calls
   *
   * @example {softswiss: '/api'}
   */
  private readonly specialUrls: { softswiss: string };
  /**
   * default domain for casino
   *
   * @example "7bitcasino.com"
   */
  private readonly defaultDomain: string;
  /**
   * default url protocol
   *
   * @example "https"
   */
  private readonly protocol: string;

  constructor(data: {
    domains: string[];
    devSubDomains: string[];
    stagingSubDomains: string[];
    prodSubDomains: string[];
    specialSubDomains: {
      softswiss: string;
      geoip: string;
      blockchain: string;
      promo: string[];
    };
    specialUrls: { softswiss: string };
    defaultDomain: string;
    protocol?: string;
  }) {
    this.domains = data.domains;
    this.devSubDomains = data.devSubDomains;
    this.stagingSubDomains = data.stagingSubDomains;
    this.prodSubDomains = data.prodSubDomains;
    this.specialSubDomains = data.specialSubDomains;
    this.specialUrls = data.specialUrls;
    this.defaultDomain = data.defaultDomain;
    this.protocol = data.protocol || 'https';
  }

  /**
   * main domains and mirrors
   */
  private get mainDomains(): string {
    return [...this.domains].join('|');
  }

  /**
   * sub domains (staging and prod) Regex string with logical OR
   */
  private get subDomains(): string {
    return [...this.devSubDomains, ...this.stagingSubDomains, ...this.prodSubDomains].join('|');
  }

  /**
   * sub domains (staging and prod) Regex string with logical OR
   */
  private get devDomains(): string {
    return [...this.devSubDomains].join('|');
  }

  /**
   * sub domains (staging and prod) Regex string with logical OR
   */
  private get stageDomains(): string {
    return [...this.devSubDomains, ...this.stagingSubDomains].join('|');
  }

  /**
   * calculated regexp for domain check with flag INSENSETIVE
   */
  private get domainRegExpI() {
    return new RegExp(`^((${this.subDomains})\\.)?(${this.mainDomains})$`);
  }

  /**
   * calculated regexp for domain check with flags GLOBAL and INSENSETIVE
   */
  private get domainRegExpGI() {
    return new RegExp(`^((${this.subDomains})\\.)?(${this.mainDomains})$`, 'gi');
  }

  /**
   * calculated regexp for stage domain check with flags GLOBAL and INSENSETIVE
   */
  private get stageDomainRegExpGI() {
    return new RegExp(`^((${this.stageDomains})\\.)(${this.mainDomains})$`, 'gi');
  }

  /**
   * calculated regexp for stage domain check with flags GLOBAL and INSENSETIVE
   */
  private get promoDomainRegExpGI() {
    return new RegExp(
      `^((${this.specialSubDomains.promo.join('|')})\\.)(${this.mainDomains})$`,
      'gi'
    );
  }

  /**
   * calculated regexp for dev domain check with flags GLOBAL and INSENSETIVE
   */
  private get devDomainRegExpGI() {
    return new RegExp(`^((${this.devDomains})\\.)(${this.mainDomains})$`, 'gi');
  }

  /**
   * method for special subdomain add
   * @param subdomain - string with subdomain
   */
  private subdomainAdd(subdomain: string): string {
    if (import.meta.browser && location && location.hostname) {
      if (this.domainRegExpI.test(location.hostname)) {
        const hostGroups = String(location.hostname).match(this.domainRegExpI);
        if (hostGroups && hostGroups[3]) {
          return `${this.protocol}://${subdomain}.${hostGroups[3]}`;
        }
      }
    }
    return `${this.protocol}://${subdomain}.${this.defaultDomain}`;
  }

  /**
   * Browser check for promo domain
   *
   * @returns true if is server mode or if domain is from staging
   */
  public get isPromoDomain(): boolean {
    if (import.meta.browser && location && location.hostname) {
      if (!this.promoDomainRegExpGI.test(location.hostname)) {
        return false;
      }
    }
    return true;
  }

  /**
   * Browser check for dev domain
   *
   * @returns true if is server mode or if domain is from staging
   */
  public get isDevDomain(): boolean {
    if (import.meta.browser && location && location.hostname) {
      if (
        this.devDomainRegExpGI.test(location.hostname) ||
        location.hostname === 'localhost' ||
        location.hostname === 'local-dev.7bitcasino.com'
      ) {
        return true;
      }
    }
    return false;
  }

  /**
   * getter which return SoftSwiss API HOST
   * example: https://www.7bitcasino.com
   *
   * IMPORTANT!
   * SS API not working with localhost, because CORS
   * thats why method returns empty string when localhost
   */
  public get ssHost(): string {
    if (import.meta.browser && location && location.hostname && location.hostname === 'localhost') {
      return '';
    } else {
      return this.subdomainAdd(this.specialSubDomains.softswiss);
    }
  }

  /**
   * getter which return SoftSwiss API URL
   * example: https://www.7bitcasino.com/api
   *
   * IMPORTANT!
   * SS API not working with localhost, because CORS
   * thats why method returns '/api' when localhost
   */
  public get ssApiUrl(): string {
    return this.ssHost + this.specialUrls.softswiss;
  }

  /**
   * This function using for domain detection and creating absolute links (for mirrors too)
   * Because we cant use localhost at SSR and we want use links to prod for
   * non full functionality sites like rc.7bitcasino.com
   *
   * @returns string with hostname and protocol (example: "https://7bicasino.com")
   */
  public get main(): string {
    if (import.meta.browser && location && location.hostname) {
      if (this.isPromoDomain) {
        return `${this.protocol}://${location.hostname.replace(/^[\w-]+\./, '')}`;
      } else if (this.domainRegExpGI.test(location.hostname)) {
        return `${this.protocol}://${location.hostname}`;
      }
    }

    return `${this.protocol}://${this.defaultDomain}`;
  }

  /**
   * getter which return url for user country detection
   */
  public get geoip(): string {
    return this.subdomainAdd(this.specialSubDomains.geoip);
  }

  /**
   * getter which return url for user country detection
   */
  public get blockchain(): string {
    return this.subdomainAdd(this.specialSubDomains.blockchain);
  }
}
